import { Avatar, Box, useTheme, Typography, Chip, Grid, Link } from "@mui/material";
import React from "react";
import { showIconByServiceId } from "../../../helper/showIconByServiceId";
import { Styles } from "./styles";
import SectionCard from "./SectionCard";

const OrderDetailsPage = ({ data = {} }) => {
    const theme = useTheme();

    const safeMap = (array, callback) => {
        if (!Array.isArray(array)) return null;
        return array.map(callback);
    };

    const getServiceTypeDisplay = (serviceType) => {
        const typeMap = {
            pin: "Pin Advertising",
            banner: "Banner Advertising",
            trust: "Trust Badge",
            tickets_ads: "Tickets Ads",
            top_result: "Top Results"
        };
        return typeMap[serviceType] || serviceType;
    };

    const getStatusColor = (status) => {
        const statusColors = {
            active: "success",
            pending: "warning",
            completed: "info",
            cancelled: "error",
        };
        return statusColors[status] || "default";
    };

    // Render logo and customLogo for services
    const renderLogoSection = (service) => {
        if (!service.logo && !service.customLogo) return null;

        return (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                    Logos:
                </Typography>
                <Grid container spacing={2}>
                    {service.logo && (
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Main Logo:
                            </Typography>
                            <img
                                src={service.logo}
                                alt="Service Logo"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 80,
                                    borderRadius: 4,
                                    objectFit: 'contain'
                                }}
                            />
                        </Grid>
                    )}
                    {service.customLogo && (
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Custom Logo:
                            </Typography>
                            {Array.isArray(service.customLogo) ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {service.customLogo.map((logo, index) => (
                                        <img
                                            key={index}
                                            src={logo}
                                            alt={`Custom Logo ${index + 1}`}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 40,
                                                borderRadius: 4,
                                                objectFit: 'contain'
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <img
                                    src={service.customLogo}
                                    alt="Custom Logo"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: 80,
                                        borderRadius: 4,
                                        objectFit: 'contain'
                                    }}
                                />
                            )}
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    };

    // Render service details card for single service orders
    const renderSingleServiceCard = (service, serviceDetails, index) => {
        const serviceType = serviceDetails?.type;
        const serviceId = serviceDetails?.id;

        return (
            <SectionCard
                key={`service-${index}`}
                title={service.name}
                subtitle={serviceType ? getServiceTypeDisplay(serviceType) : "Service"}
                sectionKey={serviceType}
                serviceId={serviceId}
                icon={showIconByServiceId(service.id)}
                status={serviceDetails?.status || data.status}
            >
                {/* Basic Service Info */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Service: {service.name}
                    </Typography>
                    {service.title && service.title !== 'not title' && (
                        <Typography variant="body1" gutterBottom>
                            Title: {service.title}
                        </Typography>
                    )}
                    {service.description && service.description !== 'not description' && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Description: {service.description}
                        </Typography>
                    )}
                    {(service.count && serviceDetails.is_days) ? (
                        <Typography variant="body2">
                            Duration: {service.count} days
                        </Typography>
                    ) : ''}
                    {(service.count && serviceDetails.is_tickets) ? (
                        <Typography variant="body2">
                            Ticket Count: {service.count}
                        </Typography>
                    ) :''}
                </Box>

                {/* Logo Section */}
                {renderLogoSection(service)}

                {/* Service Specific Details */}
                {serviceDetails && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                            Service Details:
                        </Typography>
                        {Object.entries(serviceDetails).map(([key, value]) => {
                            if (['type', 'id'].includes(key)) return null;
                            if (!value && value !== 0) return null;

                            const displayKey = key.split('_').map(word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ');

                            return (
                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {displayKey}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {key.includes('date') ? new Date(value).toLocaleDateString() : value}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </SectionCard>
        );
    };

    // Render service card for offer items
    const renderOfferServiceCard = (service, index) => {
        const serviceType = service.service_details?.type;
        const serviceId = service.service_details?.id;

        return (
            <SectionCard
                key={`offer-${service.id}`}
                title={service.our_service_name}
                subtitle={serviceType ? getServiceTypeDisplay(serviceType) : "Service"}
                sectionKey={serviceType}
                serviceId={serviceId}
                icon={showIconByServiceId(service.our_service_id)}
                status={service.status}
                isOfferItem={true}
            >
                {/* Basic Service Info */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Service: {service.our_service_name}
                    </Typography>
                    {service.title && service.title !== 'not title' && (
                        <Typography variant="body1" gutterBottom>
                            Title: {service.title}
                        </Typography>
                    )}
                    {service.description && service.description !== 'not description' && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Description: {service.description}
                        </Typography>
                    )}
                    {(service.days && service.is_days) ? (
                        <Typography variant="body2">
                            Duration: {service.days} days
                        </Typography>
                    ) : ''}
                    {(service.count_ticket && service.is_tickets) ? (
                        <Typography variant="body2">
                            Ticket Count: {service.count_ticket}
                        </Typography>
                    ) :''}
                </Box>

                {/* Logo Section */}
                {renderLogoSection(service)}

                {/* Service Specific Details */}
                {service.service_details && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                            Service Details:
                        </Typography>
                        {Object.entries(service.service_details).map(([key, value]) => {
                            if (['type', 'id'].includes(key)) return null;
                            if (!value && value !== 0) return null;

                            const displayKey = key.split('_').map(word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ');

                            return (
                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {displayKey}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {key.includes('date') ? new Date(value).toLocaleDateString() : value}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </SectionCard>
        );
    };

    return (
        <Styles>
            <Box sx={{ p: 3 }}>
                {/* Order Header */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography variant="h4" gutterBottom>
                                Order #{data.uuid}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {data.order_type === 'single_service' ? 'Single Service Order' :
                                    data.order_type === 'offer' ? 'Package Offer' : 'Custom Offer'}
                            </Typography>
                        </Box>
                        <Chip
                            label={data.status}
                            color={getStatusColor(data.status)}
                            variant="outlined"
                        />
                    </Box>

                    {/* Order Summary */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Customer</Typography>
                            <Typography variant="body2">{data.user?.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{data.user?.email}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Panel URL</Typography>
                            {data.panel_url ? (
                                <Link
                                    href={data.panel_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{
                                        wordBreak: 'break-all',
                                        color: 'primary.main',
                                        '&:hover': { color: 'primary.dark' }
                                    }}
                                >
                                    {data.panel_url}
                                </Link>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Not provided
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Order Date</Typography>
                            <Typography variant="body2">{new Date(data.created_at).toLocaleDateString()}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                            <Typography variant="body2">{data.payment_method}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                            <Chip
                                label={data.status_payment}
                                color={data.status_payment === 'paid' ? 'success' : 'warning'}
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Amount</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                ${data.price}
                                {data.discount > 0 && (
                                    <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                                        (Discount: ${data.discount})
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                        {data.paid_at && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Paid At</Typography>
                                <Typography variant="body2">{new Date(data.paid_at).toLocaleDateString()}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Services Display */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        justifyContent: "flex-start",
                    }}
                >
                    {/* Single Service Order */}
                    {data.order_type === 'single_service' && data.service && (
                        renderSingleServiceCard(data.service, data.service_details, 0)
                    )}

                    {/* Offer Order */}
                    {data.order_type === 'offer' && data.offer?.details && (
                        <>
                            {/* Offer Summary Card */}
                            <SectionCard
                                title="Offer Package"
                                subtitle={data.offer.title}
                                sectionKey="offer"
                                icon="📦"
                                status={data.status}
                            >
                                <Typography variant="body1" gutterBottom>
                                    {data.offer.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Total Price: ${data.offer.total_price}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Final Price: ${data.offer.final_price}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Includes {data.offer.details.length} services
                                </Typography>
                            </SectionCard>

                            {/* Individual Service Cards */}
                            {safeMap(data.offer.details, (service, index) =>
                                renderOfferServiceCard(service, index)
                            )}
                        </>
                    )}

                    {/* Custom Offer Order */}
                    {data.order_type === 'custom_offer' && data.custom_offer?.details && (
                        safeMap(data.custom_offer.details, (service, index) =>
                            renderOfferServiceCard(service, index)
                        )
                    )}
                </Box>

                {/* No Services Message */}
                {(!data.service && !data.offer?.details && !data.custom_offer?.details) && (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No service details available
                        </Typography>
                    </Box>
                )}
            </Box>
        </Styles>
    );
};

export default OrderDetailsPage;